import React from "react";

import { Checkbox } from "@/components/ui/checkbox";
import Dropdown from "@/components/ui/dropdown";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Tooltip from "@/components/ui/tooltip";
import {
  ArrowDown as ArrowDownIcon,
  Delete as DeleteIcon,
  Plus as PlusIcon,
} from "@/components/icons";

import {
  ApprovalAssigneeType,
  ApprovalParallelPolicy,
  ApprovalStepScopeType,
} from "../types/approval-flows.types";
import { emptyBranch, type BuilderBranch, type BuilderStep } from "../types/flow-builder.types";
import styles from "./ApprovalStepCard.module.scss";

export interface AssigneeOption {
  value: string;
  label: string;
}

/**
 * Kapsam seçenekleri — dalın onaycısı kimin talebini görebilir.
 *
 * Etiketler EÜAŞ organizasyon diliyle yazıldı: "kendi birimi" müdürlük,
 * "kök birim" Daire Başkanlığı / İşletme Müdürlüğü kademesidir.
 */
const SCOPE_OPTIONS: Array<AssigneeOption & { hint: string }> = [
  {
    value: String(ApprovalStepScopeType.REQUESTER_DEPARTMENT),
    label: "Kendi birimi (müdürlük)",
    hint: "Onaycı yalnızca kendi müdürlüğündeki personelin talebini görür ve onaylar.",
  },
  {
    value: String(ApprovalStepScopeType.REQUESTER_ROOT_DEPARTMENT),
    label: "Kendi başkanlığı / işletme müdürlüğü",
    hint: "Onaycı, bağlı olduğu başkanlığın altındaki tüm müdürlüklerin taleplerini görür.",
  },
  {
    value: String(ApprovalStepScopeType.GLOBAL),
    label: "Tüm kurum",
    hint: "Kapsam uygulanmaz. Merkezî son onay kademesi (ör. Eğitim Yöneticisi) bu olmalıdır.",
  },
];

/** Paralel kademenin ilerleme koşulu seçenekleri. */
const POLICY_OPTIONS: Array<{ value: ApprovalParallelPolicy; label: string; hint: string }> = [
  {
    value: ApprovalParallelPolicy.ANY,
    label: "Biri yeterli",
    hint: "Dallardan herhangi biri onaylayınca adım geçilir, diğerlerine sıra gelmez.",
  },
  {
    value: ApprovalParallelPolicy.ALL,
    label: "Hepsi onaylamalı",
    hint: "Her dal ayrı ayrı onaylamalıdır. Dallar arasında sıra yoktur; hangisi önce onaylarsa.",
  },
];

interface BranchFieldsProps {
  branch: BuilderBranch;
  /** Çok dallı kademede dal başlığı ayrı alan olarak gösterilir. */
  isParallel: boolean;
  roleOptions: AssigneeOption[];
  userOptions: AssigneeOption[];
  onUserSearch?: (term: string) => void;
  onChange: (patch: Partial<BuilderBranch>) => void;
  showErrors?: boolean;
}

/**
 * Tek bir dalın alanları: başlık/açıklama, onaycı tipi, rol veya kişi seçimi ve
 * onay kapsamı.
 *
 * Tek dallı kademede bu alanlar doğrudan kartın gövdesidir — paralel kademe
 * eklenmeden önceki ekranın aynısı. Çok dallı kademede her dal kendi çerçevesi
 * içinde tekrarlanır.
 */
const BranchFields: React.FC<BranchFieldsProps> = ({
  branch,
  isParallel,
  roleOptions,
  userOptions,
  onUserSearch,
  onChange,
  showErrors,
}) => {
  const isRole = branch.assigneeType === ApprovalAssigneeType.ROLE;
  const titleMissing = Boolean(showErrors) && !branch.title.trim();
  const assigneeMissing =
    Boolean(showErrors) && (isRole ? branch.roleIds.length === 0 : !branch.userId);

  /** Onaycı tipi değişince diğer alanı temizler — backend ikisinin birden dolu
   *  olmasını reddediyor. */
  const setAssigneeType = (assigneeType: ApprovalAssigneeType) => {
    onChange({
      assigneeType,
      roleIds: assigneeType === ApprovalAssigneeType.ROLE ? branch.roleIds : [],
      userId: assigneeType === ApprovalAssigneeType.USER ? branch.userId : undefined,
    });
  };

  // Çoklu seçimde etiket virgülle birleşir: "Daire Başkanı, İşletme Müdürü".
  const selectedRoleLabel = roleOptions
    .filter((o) => branch.roleIds.includes(o.value))
    .map((o) => o.label)
    .join(", ");
  const selectedUserLabel = userOptions.find((o) => o.value === branch.userId)?.label;

  const scopeValue = String(branch.scopeType);
  const selectedScope = SCOPE_OPTIONS.find((o) => o.value === scopeValue);

  return (
    <>
      <div className={styles.field}>
        <Input
          error={titleMissing}
          label={isParallel ? "Dal Adı" : "Adım Adı"}
          maxLength={200}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder={isParallel ? "Örn. Daire Başkanı Onayı" : "Örn. Birim Amiri Onayı"}
          required
          value={branch.title}
        />
      </div>

      <div className={styles.field}>
        <Textarea
          label="Açıklama"
          maxLength={2000}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Bu adımda ne değerlendirilecek?"
          rows={2}
          value={branch.description}
        />
      </div>

      <div className={styles.field}>
        {/* Onaycı tipi: sekme benzeri kompakt seçici. Seçim, altındaki
            listenin rol mü kişi mi göstereceğini belirler. */}
        <span className={styles.fieldLabel}>Onaycı Tipi</span>
        <div className={styles.segmented} role="tablist">
          <button
            aria-selected={isRole}
            className={`${styles.segment} ${isRole ? styles.segmentActive : ""}`}
            onClick={() => setAssigneeType(ApprovalAssigneeType.ROLE)}
            role="tab"
            type="button"
          >
            Rol
          </button>
          <button
            aria-selected={!isRole}
            className={`${styles.segment} ${!isRole ? styles.segmentActive : ""}`}
            onClick={() => setAssigneeType(ApprovalAssigneeType.USER)}
            role="tab"
            type="button"
          >
            Kişi
          </button>
        </div>
      </div>

      <div className={styles.field}>
        {isRole ? (
          <Dropdown
            error={assigneeMissing}
            label="Rol(ler)"
            multiple
            onSelect={(value) =>
              onChange({
                roleIds: Array.isArray(value) ? value : value ? [value as string] : [],
              })
            }
            options={roleOptions}
            placeholder="Rol seçin"
            searchable
            selectedLabel={selectedRoleLabel}
            value={branch.roleIds}
          />
        ) : (
          <Dropdown
            error={assigneeMissing}
            label="Kişi"
            onSearch={onUserSearch}
            onSelect={(value) => onChange({ userId: value as string })}
            options={userOptions}
            placeholder="Kişi seçin (aramak için yazın)"
            searchable
            selectedLabel={selectedUserLabel}
            value={branch.userId ?? null}
          />
        )}
        {isRole && (
          <p className={styles.hint}>
            Bu dalı, seçilen rollerden <strong>herhangi birine</strong> sahip kullanıcı
            onaylayabilir. Birden fazla ünvanın onay verebildiği kademeler (ör. Daire
            Başkanı / İşletme Müdürü) için birkaç rol seçin.
          </p>
        )}
        {/* Kaydedilmiş dalda hiç onaycı yoksa akış burada sessizce durur:
            talep oluşur, kimseye düşmez, kullanıcı "onay gelmiyor" der. Sunucu
            bu sayıyı detay ucunda döndürüyor; yeni eklenen dalda undefined
            olduğu için uyarı yalnızca kaydedilmiş dallarda görünür. */}
        {isRole && branch.assigneeUserCount === 0 && (
          <p className={styles.warning}>
            Bu role sahip aktif kullanıcı yok. Adım kimseye düşmez ve akış burada
            durur — role kullanıcı atayın ya da başka bir rol seçin.
          </p>
        )}
      </div>

      {/* Kapsam yalnızca ROL dallarında anlamlı: kişi ataması zaten tek bir
          kullanıcıya yapılmıştır, ayrıca departman süzmek onu erişilemez kılar. */}
      {isRole && (
        <div className={styles.field}>
          <Dropdown
            label="Onay Kapsamı"
            onSelect={(value) => onChange({ scopeType: Number(value) as ApprovalStepScopeType })}
            options={SCOPE_OPTIONS.map(({ value, label }) => ({ value, label }))}
            placeholder="Kapsam seçin"
            selectedLabel={selectedScope?.label}
            value={scopeValue}
          />
          {selectedScope && <p className={styles.hint}>{selectedScope.hint}</p>}
          {branch.scopeType === ApprovalStepScopeType.REQUESTER_DEPARTMENT && (
            <p className={styles.warning}>
              Bu kapsam müdür onayı içindir: müdür yalnızca kendi müdürlüğünün
              personelini onaylar. Her başkanlıkta tek onaycı varsa (ör. Eğitim
              Sorumlusu) “kendi başkanlığı” kapsamını seçin; aksi halde kardeş
              müdürlüklerin talepleri ona düşmez.
            </p>
          )}
        </div>
      )}

      {/* Müdürlüğe bağlı olmayan birimlerde (şeflik, servis, amirlik) müdür
          yoktur; işaretlenmezse talep bu adımda kimseye düşmeden kilitlenir. */}
      {isRole && (
        <div className={styles.field}>
          <Checkbox
            label="Onaycı bulunamazsa bu adımı atla"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ skipWhenNoApprover: e.target.checked })
            }
            value={branch.skipWhenNoApprover}
          />
          <p className={styles.hint}>
            Talep bu adıma geldiğinde kapsamda onaycı yoksa adım otomatik atlanır ve
            talep bir sonraki adıma geçer. Ör. müdürü olmayan birimlerde müdür onayı.
          </p>
        </div>
      )}
    </>
  );
};

interface ApprovalStepCardProps {
  step: BuilderStep;
  /** 0 tabanlı dizi indeksi; ekranda 1 tabanlı sıra numarası gösterilir. */
  index: number;
  total: number;
  roleOptions: AssigneeOption[];
  userOptions: AssigneeOption[];
  /** Kişi seçicide sunucu taraflı arama — personel listesi uzun olabilir. */
  onUserSearch?: (term: string) => void;
  onChange: (index: number, patch: Partial<BuilderStep>) => void;
  onBranchChange: (index: number, branchIndex: number, patch: Partial<BuilderBranch>) => void;
  onRemove: (index: number) => void;
  onMove: (index: number, direction: -1 | 1) => void;
  onInsert: (index: number, position: "BEFORE" | "AFTER") => void;
  /** Kaydet'e basıldıktan sonra eksik alanları işaretlemek için. */
  showErrors?: boolean;
}

/**
 * Tek bir onay KADEMESİNİN düzenleme kartı.
 *
 * Kademe bir ya da birden fazla DAL taşır. Tek dallı kademe klasik dikey
 * adımdır ve kart, dalın alanlarını doğrudan gösterir. İkinci dal eklendiğinde
 * kademe paralel olur: dallar yan yana (dar ekranda alt alta) çizilir ve
 * kademeye bir ilerleme koşulu (biri yeterli / hepsi onaylamalı) seçilir.
 *
 * Kademenin ayrı bir başlığı yoktur; sunucuya gönderilen kademe başlığı ilk
 * dalınkidir (bkz. toStepPayload).
 */
const ApprovalStepCard: React.FC<ApprovalStepCardProps> = ({
  step,
  index,
  total,
  roleOptions,
  userOptions,
  onUserSearch,
  onChange,
  onBranchChange,
  onRemove,
  onMove,
  onInsert,
  showErrors,
}) => {
  const isParallel = step.branches.length > 1;
  const selectedPolicy = POLICY_OPTIONS.find((o) => o.value === step.parallelPolicy);

  const addBranch = () => onChange(index, { branches: [...step.branches, emptyBranch()] });

  const removeBranch = (branchIndex: number) => {
    // Son dal silinemez: kademe en az bir onaycı taşımalı. Kademenin tamamını
    // kaldırmak için karttaki sil butonu kullanılır.
    if (step.branches.length === 1) return;
    onChange(index, { branches: step.branches.filter((_, i) => i !== branchIndex) });
  };

  return (
    <div className={styles.wrapper}>
      {/* Üstteki "araya ekle" tutamağı: bu adımın ÖNÜNE yeni adım koyar. */}
      {/* Tooltip varsayılanı "left"; bu dar tutamaklarda kartın üstüne bindiği
          için sağa alıyoruz. */}
      <Tooltip content="Bu adımın öncesine adım ekle" position="right">
        <button
          className={styles.insertHandle}
          onClick={() => onInsert(index, "BEFORE")}
          type="button"
        >
          {/* Plus ikonunun varsayılan rengi beyaz; currentColor ile butonun
              rengini (ve hover durumunu) devralsın. */}
          <PlusIcon color="currentColor" height={12} width={12} />
        </button>
      </Tooltip>

      <div className={`${styles.card} ${isParallel ? styles.cardParallel : ""}`}>
        <div className={styles.header}>
          <span className={styles.orderBadge}>{String(index + 1).padStart(2, "0")}</span>
          <span className={styles.headerHint}>
            Adım {index + 1}
            {isParallel && ` — ${step.branches.length} paralel dal`}
          </span>
          <div className={styles.headerActions}>
            <Tooltip content="Yukarı taşı">
              <button
                className={styles.iconButton}
                disabled={index === 0}
                onClick={() => onMove(index, -1)}
                type="button"
              >
                <ArrowDownIcon className={styles.arrowUp} height={16} width={16} />
              </button>
            </Tooltip>
            <Tooltip content="Aşağı taşı">
              <button
                className={styles.iconButton}
                disabled={index === total - 1}
                onClick={() => onMove(index, 1)}
                type="button"
              >
                <ArrowDownIcon height={16} width={16} />
              </button>
            </Tooltip>
            <Tooltip content={total === 1 ? "Akış en az bir adım içermeli" : "Adımı sil"}>
              <button
                className={`${styles.iconButton} ${styles.danger}`}
                disabled={total === 1}
                onClick={() => onRemove(index)}
                type="button"
              >
                <DeleteIcon height={16} width={16} />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* İlerleme koşulu yalnızca paralel kademede anlamlı: tek dallı kademede
            "biri yeterli" ile "hepsi onaylamalı" aynı şeye çıkar. */}
        {isParallel && (
          <div className={styles.policyRow}>
            <span className={styles.fieldLabel}>Bu adımın geçme koşulu</span>
            <div className={styles.segmented} role="tablist">
              {POLICY_OPTIONS.map((option) => (
                <button
                  aria-selected={step.parallelPolicy === option.value}
                  className={`${styles.segment} ${
                    step.parallelPolicy === option.value ? styles.segmentActive : ""
                  }`}
                  key={option.value}
                  onClick={() => onChange(index, { parallelPolicy: option.value })}
                  role="tab"
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
            {selectedPolicy && <p className={styles.hint}>{selectedPolicy.hint}</p>}
            <p className={styles.hint}>
              Reddetme her koşulda süreci bitirir — paralel dallar onayı paylaştırır,
              reddi paylaştırmaz.
            </p>
          </div>
        )}

        {isParallel ? (
          <div className={styles.branchGrid}>
            {step.branches.map((branch, branchIndex) => (
              <div className={styles.branchCard} key={branchIndex}>
                <div className={styles.branchHeader}>
                  <span className={styles.branchBadge}>
                    Dal {String.fromCharCode(65 + branchIndex)}
                  </span>
                  <Tooltip content="Dalı sil">
                    <button
                      className={`${styles.iconButton} ${styles.danger}`}
                      onClick={() => removeBranch(branchIndex)}
                      type="button"
                    >
                      <DeleteIcon height={14} width={14} />
                    </button>
                  </Tooltip>
                </div>
                <BranchFields
                  branch={branch}
                  isParallel
                  onChange={(patch) => onBranchChange(index, branchIndex, patch)}
                  onUserSearch={onUserSearch}
                  roleOptions={roleOptions}
                  showErrors={showErrors}
                  userOptions={userOptions}
                />
              </div>
            ))}
          </div>
        ) : (
          <BranchFields
            branch={step.branches[0]}
            isParallel={false}
            onChange={(patch) => onBranchChange(index, 0, patch)}
            onUserSearch={onUserSearch}
            roleOptions={roleOptions}
            showErrors={showErrors}
            userOptions={userOptions}
          />
        )}

        <div className={styles.branchActions}>
          <Tooltip content="Bu adıma, aynı anda karar verebilecek ikinci bir onaycı ekle">
            <button className={styles.addBranchButton} onClick={addBranch} type="button">
              <PlusIcon color="currentColor" height={14} width={14} />
              Paralel dal ekle
            </button>
          </Tooltip>
        </div>
      </div>

      {/* Alttaki tutamak: bu adımın SONRASINA yeni adım koyar. */}
      <Tooltip content="Bu adımın sonrasına adım ekle" position="right">
        <button
          className={styles.insertHandle}
          onClick={() => onInsert(index, "AFTER")}
          type="button"
        >
          {/* Plus ikonunun varsayılan rengi beyaz; currentColor ile butonun
              rengini (ve hover durumunu) devralsın. */}
          <PlusIcon color="currentColor" height={12} width={12} />
        </button>
      </Tooltip>
    </div>
  );
};

export default ApprovalStepCard;
